import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, HelpCircle, ChevronRight, LayoutDashboard, FileText, Users, Archive, CheckSquare, User, HomeIcon, Bell, X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import CreateClientWizard from '../createClientWizard/CreateClientWizard';
import PaywallModal from '../PaywallModal/PaywallModal';
import Axios from 'axios';

export default function Navbar() {
  const location = useLocation();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  // Fetch user data on mount or when wizard is opened
  useEffect(() => {
    if (!userData) {
      Axios({
        method: "GET",
        withCredentials: true,
        url: `${backEndUrl}/auth/consultantLog`,
      }).then((res) => {
        setUserData(res.data);
      }).catch((err) => {
        console.error('Failed to fetch user data:', err);
      });
    }
  }, [isWizardOpen, userData, backEndUrl]);

  const handleCloseNotification = async (notifId) => {
    try {
      await Axios({
        method: "PUT",
        withCredentials: true,
        data: {
          notif_id: notifId
        },
        url: `${backEndUrl}/updateClient/notifClosed`,
      });

      // Update local state to remove the closed notification
      setUserData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          notifications: prev.notifications.map(n => 
            n._id === notifId ? { ...n, closed: true } : n
          )
        };
      });
    } catch (error) {
      console.error("Failed to close notification:", error);
    }
  };

  const notifications = userData?.notifications?.filter(n => !n.closed) || [];

  const handleNewApplicationClick = () => {
    const demoEmails = ['john.smith@example.com', 'marie.dubois@example.com'];
    const realClients = userData?.clients?.filter(c => !c.isDemo && !demoEmails.includes(c.client_Email)) || [];
    if (userData && userData.subscription === 'free' && realClients.length >= 1) {
      setIsPaywallOpen(true);
    } else {
      setIsWizardOpen(true);
    }
  };

  const handleUpgrade = async () => {
     try {
        const response = await Axios.post(`${backEndUrl}/users/create-checkout-session`, {
          subType: 'monthly',
          consultantEmail: userData.email
        }, {
          withCredentials: true
        });
        
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (err) {
        console.error("Failed to initiate checkout:", err);
        alert("Failed to initiate checkout. Please try again.");
      }
  };

  const refreshList = () => {
    Axios({
      method: "GET",
      withCredentials: true,
      url: `${backEndUrl}/auth/consultantLog`, // Same URL as your useEffect
    }).then((res) => {
       setUserData(res.data); // <--- THIS replaces the 'temp_' row with the real DB row
       
       // Optional: Update stats
       //setCompleteNumber((res.data.clients.filter(c => c.fileStatus === 'complete' && c.archive === false)).length);
       //setLateNumber((res.data.clients.filter(c => c.fileStatus === 'late' && c.archive === false)).length);
    }).catch((err) => {
       console.error("Failed to refresh list", err);
    });
  };

 // In Navbar.js

const handleNewClientAdded = (newClientData) => {
  // Update local userData so the paywall check reflects the new client count
  setUserData(prev => prev ? { ...prev, clients: [...(prev.clients || []), newClientData] } : prev);

  // 1. Dispatch event for INSTANT "Temp" display
  window.dispatchEvent(new CustomEvent('CLIENT_ACTION', { 
      detail: { type: 'ADD', client: newClientData } 
  }));

  // 2. Poll for the Real ID
  const isTempId = newClientData._id && newClientData._id.toString().startsWith('temp_');

  if (isTempId) {
      let attempts = 0;
      const maxAttempts = 10;

      const pollForRealClient = () => {
          attempts++;
          Axios({
              method: "GET",
              withCredentials: true,
              url: `${backEndUrl}/auth/consultantLog`,
          }).then((res) => {
              const updatedData = res.data;
              const realClient = updatedData.clients.find(c =>
                  c.client_Email === newClientData.client_Email &&
                  c.client_firstName === newClientData.client_firstName &&
                  c._id && !c._id.toString().startsWith('temp_')
              );

              if (realClient) {
                  // Sync real client into userData (replace temp entry)
                  setUserData(updatedData);

                  // 3. Dispatch event to SWAP "Temp" with "Real"
                  window.dispatchEvent(new CustomEvent('CLIENT_ACTION', { 
                      detail: { type: 'UPDATE_REAL_ID', client: realClient, tempId: newClientData._id } 
                  }));

              } else if (attempts < maxAttempts) {
                  setTimeout(pollForRealClient, 1000);
              } else {
                  // Fallback: Tell homepage to just refresh everything
                  window.dispatchEvent(new CustomEvent('CLIENT_ACTION', { detail: { type: 'REFRESH_ALL' } }));
              }
          }).catch(() => {
              if (attempts < maxAttempts) setTimeout(pollForRealClient, 1000);
          });
      };
      setTimeout(pollForRealClient, 1000);
  }
};
  
  // Define page configurations based on routes
  const getPageConfig = () => {
    const path = location.pathname;
    
    // Dashboard page (index route)
    if (path === '/dashboard' || path === '/dashboard/') {
      return {
        icon: HomeIcon,
        label: 'Overview',
        path: '/dashboard'
      };
    }
    
    // Clients page
    if (path === '/dashboard/clients') {
      return {
        icon: Users,
        label: 'Clients',
        path: '/dashboard/clients'
      };
    }
    
    // Archive page
    if (path === '/dashboard/archive') {
      return {
        icon: Archive,
        label: 'Archive',
        path: '/dashboard/archive'
      };
    }
    
    // Individual client page
    if (path.startsWith('/dashboard/client/')) {
      return {
        icon: User,
        label: 'Client',
        path: path
      };
    }
    
    // Checklist page (for future implementation)
    if (path === '/dashboard/checklist' || path.startsWith('/dashboard/checklist/')) {
      return {
        icon: CheckSquare,
        label: 'Checklist',
        path: path
      };
    }
    
    // Default fallback
    return {
      icon: FileText,
      label: 'All Applications',
      path: '/dashboard'
    };
  };
  
  const pageConfig = getPageConfig();
  const IconComponent = pageConfig.icon;
  
  const getNotificationIcon = (type) => {
    if (type === 'late') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (type === 'complete') return <CheckCircle className="h-4 w-4 text-green-500" />;
    return <Info className="h-4 w-4 text-blue-500" />;
  };

  return (
    <>
      {/* This is the actual Navbar Component. 
        In your project, this would be your Navbar.js 
      */}
      <nav className="w-full bg-white/90 backdrop-blur-sm border-b border-gray-200 px-6 h-16 flex items-center justify-between sticky top-0 z-50 left-0 right-0 shadow-sm">
        
        {/* --- LEFT SIDE: CONTEXT (BREADCRUMBS) --- */}
        <div className="flex items-center gap-3 text-base text-gray-600 font-medium">
          <a 
            href="/dashboard" 
            className="flex items-center gap-2 text-gray-900 hover:text-blue-600 transition-colors"
          >
            <LayoutDashboard className="h-5 w-5 text-gray-400" />
            <span>Dashboard</span>
          </a>
          
          <ChevronRight className="h-5 w-5 text-gray-300" />
          
          <div className="flex items-center gap-2 text-gray-700">
            <IconComponent className="h-5 w-5 text-gray-400" />
            <span className="px-2 py-1 rounded-md text-gray-800">{pageConfig.label}</span>
          </div>
        </div>

        {/* --- RIGHT SIDE: ACTIONS --- */}
        <div className="flex items-center gap-3 mr-10">
          
          {/* 1. NOTIFICATIONS */}
          <div className="relative">
            <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <Bell className="h-6 w-6" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center border-2 border-white">
                  {notifications.length}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 origin-top-right">
                <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  <button onClick={() => setIsNotificationsOpen(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">
                      No new notifications
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div key={notif._id} className="px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 flex gap-3 group items-start">
                        <div className="mt-0.5 shrink-0">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 leading-tight break-words">{notif.message}</p>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseNotification(notif._id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all self-start shrink-0"
                          title="Mark as read"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 2. HELP BUTTON 
              Low friction, ghost style. 
          */}
          <a 
            href="/dashboard/support"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all group"
          >
            <HelpCircle className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
            <span className="hidden sm:inline">Help</span>
          </a>

          {/* 2. PRIMARY CTA: NEW APPLICATION 
              High contrast, clear call to action.
          */}
          <button 
            onClick={handleNewApplicationClick}
            className="flex items-center gap-2 bg-[#0a79be] hover:bg-[#0052CC] text-white px-4 py-2 rounded-lg text-m font-semibold shadow-sm hover:shadow transition-all focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="h-6 w-6" />
            <span className="font-bold">New Application</span>
          </button>

        </div>
      </nav>

      {/* Paywall Dialog */}
      <PaywallModal 
        isOpen={isPaywallOpen} 
        onClose={() => setIsPaywallOpen(false)} 
        onUpgrade={handleUpgrade} 
      />

      {/* Create Client Wizard */}
      <CreateClientWizard 
          isOpen={isWizardOpen} 
        onClose={() => setIsWizardOpen(false)}
        data={userData}
        availableTemplates={userData?.docTemplates || []}
        onCreateClient={handleNewClientAdded}
        
      />
    </>
  );
}